import React, { useMemo } from "react";

import { Box, Button, Container, IconButton, SwipeableDrawer, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import makeStyles from "hooks/makeStyles";
import useCart from "hooks/useCart";
import UtensilsIcon from "icons/UtensilsIcon";

import OrderConfirmation from "./OrderConfirmation";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		padding: 0,
		alignContent: "center",
		justifySelf: "center",
		justifyContent: "center",
		[theme.breakpoints.up(480)]: {
			flexDirection: "column"
		}
	},
	body: {
		[theme.breakpoints.up(480)]: {
			backgroundColor: theme.palette.background.default,
			color: theme.palette.text.primary,
			borderBottomLeftRadius: theme.spacing(2),
			borderBottomRightRadius: theme.spacing(2),
			padding: theme.spacing(4)
		}
	},
	closeButton: {
		marginLeft: -2,
		marginTop: -2,
		padding: 0,
		textAlign: "left",
		alignSelf: "flex-start",
		[theme.breakpoints.up(480)]: {
			display: "none"
		}
	},
	closeIcon: {
		width: 16,
		height: 16,
		color: "#fff"
	},
	drawer: {
		backgroundColor: "transparent",
		height: 100
	},
	drawerBottom: {
		borderTopLeftRadius: 5,
		borderTopRightRadius: 5
	},
	paper: {
		borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px 0 0`,
		backgroundColor: "rgba(33,33,33,0.7)",
		WebkitBackdropFilter: "blur(5px)",
		backdropFilter: "blur(5px)",
		color: "#fff",
		padding: theme.spacing(2),
		display: "flex",
		height: 300,
		[theme.breakpoints.up(480)]: {
			height: "100vh",
			borderRadius: "0",
			width: "50%",
			maxWidth: "442px",
			left: "auto",
			backgroundColor: theme.palette.background.paper,
			padding: 0,
			color: theme.palette.text.primary
		}
	},
	backdrop: {
		backgroundColor: "rgba(0,0,0,0.1)",
		[theme.breakpoints.up(480)]: {
			backgroundColor: "transparent",
			WebkitBackdropFilter: "blur(1.8px)",
			backdropFilter: "blur(1.8px)"
		}
	},
	drawerContainer: {
		WebkitBackdropFilter: "blur(5px)",
		backdropFilter: "blur(5px)",
		color: "#fff",
		padding: theme.spacing(0.5),
		display: "flex"
	},
	footer: {
		backgroundColor: "transparent",
		bottom: 0,
		left: 0,
		right: 0,
		width: "100%",
		padding: theme.spacing(2),
		display: "flex",
		position: "absolute",
		[theme.breakpoints.up(480)]: {
			marginTop: 0,
			width: "auto",
			flexDirection: "column",
			flex: "1",
			justifyContent: "center",
			padding: theme.spacing(2)
		}
	},
	actionButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		borderRadius: 24,
		fontSize: 16,
		height: 48,
		width: "100%",
		padding: 0,
		alignContent: "center",
		justifyContent: "center",
		textTransform: "uppercase",
		"&:hover": {
			[theme.breakpoints.up(480)]: {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.primary.contrastText
			}
		}
	}
}));

interface Props {
	open: boolean;
}

const MessageDrawer = ({ open }: Props) => {
	const classes = useStyles();
	const { hideOrderConfirmation } = useCart();
	const { t } = useTranslation();

	const onClose = () => {
		hideOrderConfirmation();
	};

	const onBottomDrawerOpened = () => {};

	return (
		<SwipeableDrawer
			onOpen={onBottomDrawerOpened}
			PaperProps={{ className: classes.paper }}
			ModalProps={{
				BackdropProps: {
					className: classes.backdrop
				}
			}}
			className={clsx(classes.root)}
			anchor={"bottom"}
			open={open}
			onClose={onClose}
		>
			<Container className={classes.root}>
				<IconButton className={classes.closeButton} onClick={onClose}>
					<Close className={classes.closeIcon} />
				</IconButton>
				<Box className={classes.body}>
					<OrderConfirmation />
				</Box>
				<Box className={classes.footer}>
					<Button variant="outlined" className={classes.actionButton} onClick={onClose}>
						<Typography>{t("returnToMenu")}</Typography>
					</Button>
				</Box>
			</Container>
		</SwipeableDrawer>
	);
};

export default MessageDrawer;
